(function () {
  'use strict';

  /* ─── Config ──── */
  const LS_KEY        = 'shopify_wishlist';
  const SYNC_DEBOUNCE = 1200;         
  const CUSTOMER_ID   = window.__wishlistCustomerId || null;  
  const IS_LOGGED_IN  = !!CUSTOMER_ID;

  /* ─── Internal state ─────────── */
  let _items      = [];  
  let _syncTimer  = null;
  let _listeners  = [];   

  /* ════ LOCAL STORAGE helpers══════ */
  function lsRead() {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function lsWrite(ids) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(ids)); } catch {}
  }

  const PROXY_ENABLED = false; 
  const PROXY_URL     = '/apps/wishlist-proxy';

  async function metafieldRead() {
    if (!IS_LOGGED_IN || !PROXY_ENABLED) return null;
    try {
      const res  = await fetch(`${PROXY_URL}?action=get`, { credentials: 'same-origin' });
      const data = await res.json();
      return Array.isArray(data.ids) ? data.ids.map(String) : null;
    } catch {
      return null;
    }
  }

  async function metafieldWrite(ids) {
    if (!IS_LOGGED_IN || !PROXY_ENABLED) return;
    try {
      await fetch(PROXY_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'set', ids }),
      });
    } catch {}
  }

  /* ─── Debounced sync push ─── */
  function scheduleSync() {
    if (!IS_LOGGED_IN || !PROXY_ENABLED) return;
    clearTimeout(_syncTimer);
    _syncTimer = setTimeout(() => metafieldWrite([..._items]), SYNC_DEBOUNCE);
  }

  /* ═CORE API═════ */
  function _notify() {
    _listeners.forEach(fn => fn([..._items]));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: [..._items] } }));
  }

  function has(productId) {
    return _items.includes(String(productId));
  }

  function add(productId) {
    const id = String(productId);
    if (!_items.includes(id)) {
      _items.push(id);
      lsWrite(_items);
      scheduleSync();
      _notify();
    }
  }

  function remove(productId) {
    const id  = String(productId);
    const idx = _items.indexOf(id);
    if (idx !== -1) {
      _items.splice(idx, 1);
      lsWrite(_items);
      scheduleSync();
      _notify();
    }
  }

  function toggle(productId) {
    has(productId) ? remove(productId) : add(productId);
    return has(productId);
  }

  function getAll() {
    return [..._items];
  }

  function count() {
    return _items.length;
  }

  function onChange(fn) {
    _listeners.push(fn);
    return () => { _listeners = _listeners.filter(f => f !== fn); }; // unsubscribe
  }

  /* ═══ INIT — merge localStorage + metafield ═════ */
  async function init() {
    const local = lsRead();

    if (IS_LOGGED_IN && PROXY_ENABLED) {
      const remote = await metafieldRead();
      if (remote) {
        _items = [...new Set([...remote, ...local])];
      } else {
        _items = local;
      }
      if (_items.length) metafieldWrite([..._items]);
    } else {
      _items = local;
    }

    lsWrite(_items);
    _notify();
    _updateAllButtons();
    _updateCountBadges();
  }

  /* ═══DOM HELPERS — update buttons & badges═══ */
  function _updateAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id = String(btn.dataset.productId);
      _applyButtonState(btn, has(id));
    });
  }

  function _updateCountBadges() {
    const n = count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
  }

  function _applyButtonState(btn, wished) {
    btn.classList.toggle('is-wished', wished);
    btn.setAttribute('aria-pressed', wished ? 'true' : 'false');
    const label = btn.dataset.productTitle
      ? (wished ? `Remove ${btn.dataset.productTitle} from wishlist` : `Add ${btn.dataset.productTitle} to wishlist`)
      : (wished ? 'Remove from wishlist' : 'Add to wishlist');
    btn.setAttribute('aria-label', label);
  }

  /* ─── Listen for change → update all UI ───── */
  onChange(() => {
    _updateAllButtons();
    _updateCountBadges();
  });

  /* ══ CLICK DELEGATION — works for dynamically added buttons too═══ */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const id     = btn.dataset.productId;
    const wished = toggle(id);
    _applyButtonState(btn, wished);


    btn.classList.add('wishlist-pop');
    btn.addEventListener('animationend', () => btn.classList.remove('wishlist-pop'), { once: true });
  });

  /* ══════EXPOSE══════ */
  window.Wishlist = { has, add, remove, toggle, getAll, count, onChange, init };

  /* ─── Auto-init on DOMContentLoaded ──────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
